
import { useState, useEffect, useCallback, useRef } from 'react';
import { TodayAnalytics, RecentCall } from '@/services/api/callAnalytics';
import { activityApi } from '@/services/api/activityApi';
import { useAuth } from '@/context/AuthContext';

// Request deduplication cache
const analyticsRequestCache = new Map<string, Promise<any>>();
const CACHE_DURATION = 120000; // 2 minutes - increased from 30 seconds
const dataCache = new Map<string, { data: any; timestamp: number }>(); // Data cache for memoization

interface UseCallAnalyticsOptions {
  shouldFetch?: boolean;
}

export const useCallAnalytics = (options: UseCallAnalyticsOptions = {}) => {
  const { shouldFetch = true } = options;
  const { user } = useAuth();
  const [todayAnalytics, setTodayAnalytics] = useState<TodayAnalytics>({
    total_calls: 0,
    success_rate: 0,
    avg_duration: 0,
    total_duration: 0,
    call_quality: 80
  });
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchTime = useRef<number>(0);
  const currentUserId = useRef<number | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!user?.id || !shouldFetch) {
      console.log('useCallAnalytics: Skipping fetch - no user ID or fetch disabled');
      return;
    }

    // Prevent duplicate calls for the same user within cache duration
    const now = Date.now();
    const cacheKey = `analytics-${user.id}`;
    const dataCacheKey = `data-${user.id}`;
    
    // Check data cache first
    const cachedData = dataCache.get(dataCacheKey);
    if (cachedData && now - cachedData.timestamp < CACHE_DURATION) {
      const cacheAge = Math.round((now - cachedData.timestamp) / 1000);
      console.log(`🕐 useCallAnalytics: Using cached data (${cacheAge}s old) - Today: ${cachedData.data.todayAnalytics.total_calls} calls`);
      setTodayAnalytics(cachedData.data.todayAnalytics);
      setRecentCalls(cachedData.data.recentCalls);
      return;
    }
    
    if (
      currentUserId.current === user.id && 
      now - lastFetchTime.current < CACHE_DURATION
    ) {
      console.log('useCallAnalytics: Using time-based cache, skipping API call');
      return;
    }

    // Check if request is already in progress
    if (analyticsRequestCache.has(cacheKey)) {
      console.log('useCallAnalytics: Request already in progress, waiting...');
      await analyticsRequestCache.get(cacheKey);
      return;
    }

    console.log('useCallAnalytics: Fetching analytics for user:', user.id);
    setLoading(true);
    setError(null);

    const fetchPromise = (async () => {
      try {
        const [activityStats, activityData] = await Promise.all([
          activityApi.getTodayActivityStats(user.id),
          activityApi.getRecentUserActivities(user.id, 20)
        ]);

        // Transform activity stats to TodayAnalytics format
        const todayData: TodayAnalytics = {
          total_calls: activityStats.activity_metrics.calls_attempted,
          success_rate: activityStats.activity_metrics.calls_attempted > 0 
            ? (activityStats.activity_metrics.calls_reached / activityStats.activity_metrics.calls_attempted) * 100 
            : 0,
          avg_duration: 0, // Activity API doesn't provide avg duration
          total_duration: 0, // Activity API doesn't provide total duration
          call_quality: 80 // Default value since not available in activity API
        };

        // Transform call activities to RecentCall format
        const recentData: RecentCall[] = activityData.activities
          .filter(activity => activity.activity_type === 'call')
          .map(activity => ({
            time: activity.activity_date,
            duration: activity.duration_minutes ? `${activity.duration_minutes}m` : '0m',
            outcome: activity.activity_status || 'unknown',
            quality: 'Good', // Default quality since not available in activity API
            phone_number: extractPhoneFromDescription(activity.activity_description || '')
          }));

        // Update state
        setTodayAnalytics(todayData);
        setRecentCalls(recentData);
        
        // Cache the data
        dataCache.set(dataCacheKey, {
          data: { todayAnalytics: todayData, recentCalls: recentData },
          timestamp: now
        });
        
        lastFetchTime.current = now;
        currentUserId.current = user.id;
        console.log('useCallAnalytics: Successfully fetched and cached analytics data');
      } catch (err) {
        console.error('useCallAnalytics: Error fetching call analytics:', err);
        setError('Failed to load analytics data');
        
        // Exponential backoff on error
        setTimeout(() => {
          if (user?.id && shouldFetch) {
            fetchAnalytics();
          }
        }, Math.min(5000 * Math.pow(2, Math.random()), 30000));
      } finally {
        setLoading(false);
        analyticsRequestCache.delete(cacheKey);
      }
    })();

    analyticsRequestCache.set(cacheKey, fetchPromise);
    await fetchPromise;
  }, [user?.id, shouldFetch]);

  useEffect(() => {
    console.log('useCallAnalytics: useEffect triggered with user:', user?.id, 'shouldFetch:', shouldFetch);
    if (shouldFetch && user?.id) {
      fetchAnalytics();
    }
  }, [user?.id, shouldFetch, fetchAnalytics]);

  // Stable refetch function
  const stableRefetch = useCallback(() => {
    // Clear caches to force fresh data
    const dataCacheKey = `data-${user?.id}`;
    dataCache.delete(dataCacheKey);
    lastFetchTime.current = 0;
    
    return fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    todayAnalytics,
    recentCalls,
    loading,
    error,
    refetch: stableRefetch
  };
};

// Helper function to extract phone number from activity description
const extractPhoneFromDescription = (description: string): string => {
  // Look for phone number patterns in the description
  const phoneRegex = /\+[\d\s-()]+/;
  const match = description.match(phoneRegex);
  return match ? match[0] : '';
};
